/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
import { IExperimentationProvider, IExperiments, IVariants, IVariations, State, IExperimentsResult, ISecretManager } from '../../interface/';
import * as optimizely from '@optimizely/optimizely-sdk';
import axios from 'axios';

/**
 * An implementation of ExperimentationProvider interface for optimizely connector
 */
class OptimizelyConnector implements IExperimentationProvider {
    private optimizelyClientInstance: optimizely.Client | undefined;
    private secretManager!: ISecretManager;
    private projectId: string = '';
    private currentProjectSdkKey: string = '';
    private mapToIExperimentationStatus = (status: string): State => {
        switch (status) {
            case 'running': return State.Running;
            case 'paused': return State.Paused;
            case 'archived': return State.Archived;
            case 'campaign_paused': return State.Paused;
            default: return State.Draft;
        }
    }

    public async initialize(config: any, secretManager: ISecretManager): Promise<boolean> {
        // Refresh time is provided in seconds to match consistency
        // of other cache settings but optimizely expects ms
        this.optimizelyClientInstance = optimizely.createInstance({
            sdkKey: config.sdkKey,
            datafileOptions: {
                autoUpdate: true,
                updateInterval: (+config.refreshTime) * 1000 || 1800000
            }
        });
        this.currentProjectSdkKey = config.sdkKey;
        this.secretManager = secretManager;
        this.projectId = config.projectId;
        try {
            const ready = await this.optimizelyClientInstance.onReady();
            if (!ready.success) {
                throw new Error(ready.reason ? ready.reason : 'connector initialization fails');
            }
        } catch (err) {
            throw err;
        }
        return true;
    }

    public async getExperiments(retailServerBaseUrl: string, connectorName: string, page?: string, items?: string): Promise<IExperimentsResult> {
        if (!retailServerBaseUrl) {
            throw new Error('Retail server base URL not provided.');
        }

        if (!this.projectId) {
            throw new Error('Project Id not provided.');
        }

        let getExperimentsUrl = new URL(`https://api.optimizely.com/v2/experiments?project_id=${this.projectId}`);
        if (page) {
            getExperimentsUrl.searchParams.append("page", page);
        }

        if (items) {
            // Adding a fallback to 100 experimens when items > 100 
            // as Optimizely returns a maximum of 100 experiments per request 
            if (parseInt(items) > 100) {
                items = '100';
            }
            getExperimentsUrl.searchParams.append("per_page", items)
        }

        const experimentList: IExperiments[] = [];
        let experimentsResult: IExperimentsResult = {
            name: connectorName,
            experiments: experimentList
        };

        const secretVal = await this.secretManager.getSecretValue('OptimizelyAccessToken', retailServerBaseUrl);

        if(!secretVal.value) {
            throw new Error('Could not fetch the secret value! Please make sure the secret value exists in your key vault');
        }


        const res = await axios.get(getExperimentsUrl.href,
            {
                headers:
                    { Authorization: `Bearer ${secretVal.value}` }
            });

        const data = res.data ? res.data : [];
        if (data && Array.isArray(data)) {
            for (let i = 0; i < data.length; i++) {
                const variations: IVariations[] = [];
                const experiments = data[i];
                const experiment_variations = experiments.variations;

                // Extract the required variation properties first
                if (experiment_variations.length > 0) {
                    for (let j = 0; j < experiment_variations.length; j++) {
                        // Map the status of variation for an experiment received from Optimizely to IExperimentation- Status
                        const variationStatus = this.mapToIExperimentationStatus(experiment_variations[j].status);
                        let variationName = experiment_variations[j].name || experiment_variations[j].key;

                        const variation: IVariations = {
                            friendlyName: variationName,
                            id: (experiment_variations[j].variation_id).toString(),
                            status: variationStatus,
                            weight: (experiment_variations[j].weight).toString()
                        }
                        variations.push(variation);
                    }
                }

                // Map the status of experiment received from Optimizely to IExperimentation- Status
                const experimentStatus = this.mapToIExperimentationStatus(experiments.status);
                const experimentUrl = `https://app.optimizely.com/v2/projects/${experiments.project_id}/experiments/${experiments.id}/variations`;
                const experimentResultUrl = `https://app.optimizely.com/v2/projects/${experiments.project_id}/results/experiments/${experiments.id}`;

                const experiment: IExperiments = {
                    friendlyName: experiments.name,
                    description: experiments.description,
                    type: experiments.type,
                    id: (experiments.id).toString(),
                    status: experimentStatus,
                    variations: variations,
                    link: experimentUrl,
                    createdDate: experiments.created,
                    lastModifiedDate: experiments.last_modified,
                    lastModifiedBy: '',
                    resultLink: experimentResultUrl
                };
                experimentList.push(experiment);
                experimentsResult = {
                    name: connectorName,
                    experiments: experimentList
                };
            }
        }
        return Promise.resolve(experimentsResult);
    }

    public async getConfigForClientSideInit(): Promise<any> {
        if (!this.currentProjectSdkKey) {
            return {}
        }
        const DATAFILE_URL = `https://cdn.optimizely.com/datafiles/${this.currentProjectSdkKey}.json`;
        const datafile = await axios.get(DATAFILE_URL, {
            headers: {
                'Content-Type': 'application/json',
            }
        });
        return datafile.data;
    }

    public initializeClientSide(config: any): boolean {
        this.optimizelyClientInstance = optimizely.createInstance(
            { datafile: config }
        );
        return true;
    }

    public getVariantsForUser(userId: string, attributes?: { [index: string]: string } | undefined): IVariants[] {
        if (!this.optimizelyClientInstance) {
            console.error('the connector is not initilized')
            return [];
        }
        const config = this.optimizelyClientInstance.getOptimizelyConfig();
        const experimentsMap = config && config.experimentsMap;
        if (!experimentsMap) {
            return [];
        }
        let variantsList: IVariants[] = []
        Object.keys(experimentsMap).forEach((expKey) => {
            const experiment = experimentsMap[expKey];
            const variationMap = experiment.variationsMap;
            const variation = Object.values(variationMap);
            const variationKey = this.optimizelyClientInstance ? this.optimizelyClientInstance.getVariation(expKey, userId, attributes) : null;

            for (let i = 0; i < variation.length; i++) {
                const variationId = variation[i].id;
                if (variation[i].key === variationKey) {
                    variantsList.push({ variantId: variationId, experimentId: experiment.id });
                    break;
                }
            }
        });
        return variantsList;
    }

    public activateExperiment(userId: string, experiments: IVariants[], attributes?: { [index: string]: string } | undefined): boolean {
        if (this.optimizelyClientInstance) {
            // Build mapping of experiment id to experiment key
            const expIdToExpKey = {};
            const config = this.optimizelyClientInstance.getOptimizelyConfig();
            const experimentsMap = config && config.experimentsMap;
            if (!experimentsMap) {
                return false;
            }
            Object.keys(experimentsMap).forEach((expKey) => {
                const experiment = experimentsMap[expKey];
                expIdToExpKey[experiment.id] = expKey;
            });

            for (let i = 0; i < experiments.length; i++) {
                const experimentKey = expIdToExpKey[experiments[i].experimentId];
                this.optimizelyClientInstance.activate(experimentKey, userId, attributes);
            }
        }
        return true;
    }

    public validateRegistration(config: any): boolean {
        return true;
    }
}

const connector = new OptimizelyConnector();
export default connector;
